import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { normalizeImageUrl } from "@/lib/image-url";
import { MapPin, Star, Clock, MessageCircle, Share2, Building2, ShieldCheck, ExternalLink } from "lucide-react";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";

export interface BizItem {
  id: string;
  businessName: string;
  businessNameKo?: string;
  description?: string;
  images?: string[];
  state?: string;
  city?: string;
  suburb?: string;
  // Phase 3.6: 비즈홍보 전용 필드
  category?: string; // 식당, 클리닉, 미용, 수리, 병원 등
  abn?: string;
  verifiedABN?: boolean;
  kakaoChannel?: string; // 카카오채널 URL
  operatingHours?: string; // 영업시간
  reviewCount?: number;
  averageRating?: number;
  membershipTier?: string; // premium, platinum
  contactMethod?: string;
  phoneNumber?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
  views?: number;
  favorites?: number;
  distance?: number; // 거리 (km)
}

interface BizCardProps {
  item: BizItem;
  onInquire?: () => void;
}

export function BizCard({ item, onInquire }: BizCardProps) {
  const timeAgo = formatDistanceToNow(new Date(item.createdAt), {
    addSuffix: true,
    locale: ko,
  });

  const displayName = item.businessNameKo || item.businessName;

  return (
    <Link href={`/biz/${item.id}`}>
      {/* Phase 3.6: Phase 2 스타일 카드 적용 */}
      <Card className="rounded-xl border-0 shadow-none hover:scale-[1.02] transition-transform overflow-hidden h-full flex flex-col cursor-pointer">
        {/* Image - 16:9 ratio */}
        <div className="relative aspect-video bg-muted overflow-hidden">
          {item.images && item.images.length > 0 ? (
            <img
              src={normalizeImageUrl(item.images[0]) || item.images[0]}
              alt={displayName}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="w-16 h-16 text-muted-foreground/30" />
            </div>
          )}
          {/* Unified Business Premium badge (legacy Platinum is equivalent) */}
          {(item.membershipTier === "premium" || item.membershipTier === "platinum") && (
            <Badge className="absolute top-2 left-2 bg-brand-orange">
              프리미엄
            </Badge>
          )}
        </div>

        <div className="flex flex-col flex-1 p-4">
          {/* Title - font-bold, line-clamp-2 */}
          <CardHeader className="space-y-2 pb-3 p-0">
            <h3 className="font-bold line-clamp-2 text-lg leading-tight">
              {displayName}
            </h3>
          </CardHeader>

          {/* Meta 정보 */}
          <CardContent className="flex-1 space-y-2 p-0">
            {/* Location, Distance, TimeAgo */}
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              {item.suburb && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>
                    {item.suburb}
                    {item.city ? `, ${item.city}` : ""}
                  </span>
                </div>
              )}
              {item.distance !== undefined && (
                <div className="flex items-center gap-1">
                  <span>{item.distance}km</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <span>{timeAgo}</span>
              </div>
            </div>

            {/* Phase 3.6: 비즈홍보 전용 메타 - 카테고리, ABN 인증, 카카오채널, 영업시간 */}
            <div className="flex items-center gap-2 flex-wrap">
              {item.category && (
                <Badge variant="outline" className="text-xs">
                  {item.category}
                </Badge>
              )}
              {/* Phase 3.6: ABN 인증 배지 (필수) */}
              {item.verifiedABN && (
                <Badge className="bg-trust-verified/10 text-trust-verified border-trust-verified/20 text-xs">
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  ABN 인증
                </Badge>
              )}
            </div>

            {/* Phase 3.6: 카카오채널 */}
            {item.kakaoChannel && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MessageCircle className="h-3 w-3" />
                <a
                  href={item.kakaoChannel}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-brand-primary hover:underline flex items-center gap-1"
                >
                  카카오채널
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            {/* Phase 3.6: 영업시간 */}
            {item.operatingHours && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{item.operatingHours}</span>
              </div>
            )}

            {/* 후기 및 평점 */}
            {item.reviewCount !== undefined && item.reviewCount > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <span>
                  {item.averageRating?.toFixed(1) || "0.0"} ({item.reviewCount}개 후기)
                </span>
              </div>
            )}

            {/* Description */}
            {item.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                {item.description}
              </p>
            )}
          </CardContent>

          {/* Footer - CTA: "문의하기" */}
          <CardFooter className="flex items-center justify-between text-sm text-muted-foreground pt-2 p-0 border-t border-line-subtle mt-2">
            <div className="flex items-center gap-3">
              {item.views !== undefined && item.views !== null && item.views > 0 && (
                <div className="flex items-center gap-1">
                  <span>{item.views.toLocaleString()} views</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Phase 3.6: 문의하기 버튼 */}
              <Button
                size="sm"
                className="bg-brand-orange hover:bg-brand-orange/90"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onInquire) {
                    onInquire();
                  } else if (item.kakaoChannel) {
                    window.open(item.kakaoChannel, "_blank");
                  } else {
                    // TODO: 내장 채팅 시작
                    window.location.href = `/chat-start/biz/${item.id}`;
                  }
                }}
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                문의하기
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // TODO: 공유 로직
                }}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </CardFooter>
        </div>
      </Card>
    </Link>
  );
}
